import { useState } from 'react'
import { STATUS_COLORS } from '../lib/constants'

export function Badge({ status, text }) {
  const t = text || status
  const color = STATUS_COLORS[t] || 'gray'
  return <span className={`badge badge-${color}`}>{t}</span>
}

export function MarketBadge({ market }) {
  return <span className="badge badge-market">{market}</span>
}

export function Spinner() {
  return <div className="loading"><div className="spinner" /><p style={{color:'#64748B'}}>Loading...</p></div>
}

export function Modal({ title, onClose, children, footer }) {
  return (
    <div className="modal-overlay">
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">{title}</span>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  )
}

export function Field({ label, children }) {
  return <div className="form-group"><label>{label}</label>{children}</div>
}

export function Input({ label, ...props }) {
  return <Field label={label}><input {...props} /></Field>
}

export function Select({ label, options, ...props }) {
  return (
    <Field label={label}>
      <select {...props}>
        <option value="">— Select —</option>
        {options.map(o => typeof o === 'string'
          ? <option key={o} value={o}>{o}</option>
          : <option key={o.value} value={o.value}>{o.label}</option>
        )}
      </select>
    </Field>
  )
}

export function Textarea({ label, ...props }) {
  return <Field label={label}><textarea {...props} /></Field>
}

export function ReadOnly({ label, value }) {
  return (
    <Field label={label}>
      <input className="input-readonly" readOnly value={value || '—'} />
    </Field>
  )
}

export function HtmlCell({ has, id, which, canEdit, onChanged }) {
  const [busy, setBusy] = useState(false)
  const download = async (e) => {
    e.stopPropagation(); setBusy(true)
    try {
      const { api } = await import('../lib/api')
      const r = await api.getProductHtml(id, which)
      const blob = new Blob([r.html || ''], { type: 'text/html' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url; a.download = `${which}_${id}.html`
      document.body.appendChild(a); a.click(); document.body.removeChild(a)
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    } catch { alert('Could not load HTML') }
    setBusy(false)
  }
  const upload = (e) => {
    e.stopPropagation()
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = async () => {
      setBusy(true)
      try {
        const { api } = await import('../lib/api')
        const field = which === 'sales' ? 'comp_sales_html' : 'comp_adv_html'
        await api.setProductHtml(id, field, String(reader.result || ''))
        if (onChanged) onChanged()
      } catch (err) { alert('Upload failed: ' + err.message) }
      setBusy(false)
    }
    reader.readAsText(file)
    e.target.value = ''
  }
  const base = { fontSize:11, fontWeight:600, padding:'4px 12px', borderRadius:6, cursor:'pointer', whiteSpace:'nowrap', display:'inline-flex', alignItems:'center', gap:5 }
  if (has) {
    return (
      <button onClick={download} disabled={busy} title="Download the saved HTML"
        style={{...base, background:'rgba(16,185,129,0.10)', border:'1px solid rgba(16,185,129,0.4)', color:'#10B981'}}>
        {busy ? '…' : '⬇ Download'}
      </button>
    )
  }
  if (canEdit) {
    return (
      <label onClick={e=>e.stopPropagation()} title="Upload an .html file"
        style={{...base, background:'var(--bg3)', border:'1px dashed var(--border2)', color:'var(--text3)'}}>
        {busy ? '…' : '⬆ Upload'}
        <input type="file" accept=".html,.htm,text/html" onChange={upload} style={{display:'none'}} />
      </label>
    )
  }
  return <span style={{color:'#94A3B8'}}>—</span>
}
export function UrlCopyCell({ url }) {
  const [copied, setCopied] = useState(false)
  if (!url) return <span style={{color:'#94A3B8'}}>—</span>
  const href = /^https?:\/\//.test(url) ? url : 'https://' + url
  const copy = (e) => {
    e.stopPropagation()
    navigator.clipboard.writeText(url).then(() => { setCopied(true); setTimeout(()=>setCopied(false),1500) })
  }
  return (
    <div style={{display:'flex',flexDirection:'column',gap:4,minWidth:0}}>
      <a href={href} target="_blank" rel="noreferrer" onClick={e=>e.stopPropagation()}
        style={{color:'#1E6FBF',fontSize:11,textDecoration:'none',wordBreak:'break-all',lineHeight:1.4}}>{url}</a>
      <button onClick={copy} style={{alignSelf:'flex-start',fontSize:10,fontWeight:600,padding:'2px 8px',borderRadius:5,border:'1px solid var(--border2)',background:copied?'var(--accent3)':'var(--bg3)',color:copied?'#000':'var(--accent)',cursor:'pointer'}}>{copied?'✓ Copied':'📋 Copy'}</button>
    </div>
  )
}
export function LinkCell({ url }) {
  if (!url) return <span style={{color:'#94A3B8'}}>—</span>
  return <a href={url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{color:'#1E6FBF',textDecoration:'none'}}>🔗 Link</a>
}

export function Empty({ icon = '📭', title = 'No data yet', sub = '' }) {
  return (
    <div className="empty">
      <div className="empty-icon">{icon}</div>
      <h3>{title}</h3>
      {sub && <p>{sub}</p>}
    </div>
  )
}
